/**
 * "What changed since the previous underwrite" — audit finding R1, on the report.
 *
 * A returning client asks one question on every visit: *what changed?* Until now the product had no
 * answer to it, and a client whose deal had re-underwritten twice since their last visit got the
 * same page as someone opening it for the first time.
 *
 * ⚠️ THE BASELINE IS THE PREVIOUS PUBLISHED VERSION, NOT THE READER'S LAST VISIT (Brad, 2026-08-20).
 * Per-reader "new to you" needs per-user read state that does not exist; this needs none and works
 * for every reader the day it ships. The two are not exclusive — per-reader becomes cheap once usage
 * tracking lands — but the deal-level answer is the one the deal itself poses.
 *
 * ⚠️ A CHANGE IS A CHANGE IN WHAT THE READER SEES, and that is why every comparison runs over the
 * FORMATTED string rather than the raw number. Compare raw and a DSCR of 1.3099999 against 1.31
 * reports a change the page cannot show; round separately for display and the two can drift apart
 * silently. Formatting first makes the two impossible to disagree: this block can never claim a
 * movement the figures below do not show, nor stay silent about one they do.
 *
 * 🔴 AND A MISSING VALUE IS NOT AN UNCHANGED ONE. Each field is compared only when BOTH sides state
 * it; a field either side is silent about is reported as not comparable, by name. Formatters render
 * null as an em dash, so a string comparison alone would read two absences as agreement and publish
 * a steadiness nobody measured — the same false all-clear `CurrencyBadge` exists to refuse.
 */

import { useNavigate } from 'react-router-dom';
import {
  isIngestionResult,
  type AnalysisResult,
  type PreviousUnderwrite,
  type PremiumDealAnalysis,
} from '../api.js';
import { integer, multiple, percent, timestamp } from '../format.js';

/** What the block compares. Ordered as it renders: the conclusion, then the figures behind it. */
const FIELDS = [
  'recommendation',
  'overallScore',
  'dscr',
  'yieldOnCostPct',
  'documentCount',
] as const;

export type ChangedField = (typeof FIELDS)[number];

const LABELS: Record<ChangedField, string> = {
  recommendation: 'Verdict',
  overallScore: 'Score',
  dscr: 'DSCR',
  yieldOnCostPct: 'Yield on cost',
  documentCount: 'Documents read',
};

/**
 * Why one side of a comparison was silent. Named per field rather than lumped into one line, because
 * "the earlier run stated no DSCR" and "this run states no DSCR" send a reader to different places.
 */
type Absence = 'absent-then' | 'absent-now' | 'absent-both';

export interface FieldChange {
  field: ChangedField;
  label: string;
  from: string;
  to: string;
}

export interface FieldNotComparable {
  field: ChangedField;
  label: string;
  why: Absence;
}

export interface UnderwriteComparison {
  changes: FieldChange[];
  notComparable: FieldNotComparable[];
}

/** The five figures, as this block reads them off either side. Null means the run did not state it. */
export interface UnderwriteFigures {
  recommendation: string | null;
  overallScore: number | null;
  dscr: number | null;
  yieldOnCostPct: number | null;
  documentCount: number | null;
}

/**
 * How each field is rendered. The comparison runs over these outputs, so they are the definition of
 * "changed" as well as of "shown" — see the header.
 *
 * ⚠️ `overallScore` is `String(n)` and NOT `integer(n)`, which rounds. The headline draws the score
 * bare (`{score?.overallScore}`), so rounding here would let a 48.4 → 48.6 movement — visible on the
 * page — be reported as no change at all. Match the reader's view, whatever the reader's view is.
 */
const FORMAT: Record<ChangedField, (v: string | number) => string> = {
  recommendation: (v) => String(v),
  overallScore: (v) => String(v),
  dscr: (v) => multiple(Number(v), 2),
  yieldOnCostPct: (v) => percent(Number(v), 2),
  documentCount: (v) => integer(Number(v)),
};

/**
 * Read the five figures off a run's `result`.
 *
 * ⚠️ BOTH STORED SHAPES. A document run nests the bundle under `analysis`; a manual `POST /analyses`
 * run stores it bare. Reading only the wrapper would compare every manual run against nothing, and
 * the wrapper case passing its tests is exactly what would make that look covered
 * (`redactAnalysisResult`'s docstring names the same trap on the server).
 *
 * `recommendation ?? verdict` is permanent: stored analyses are never migrated in place, so a run
 * from before the alias was dropped (ADR-0035 §9) carries only `verdict`.
 */
export function figuresOf(result: AnalysisResult | null | undefined): UnderwriteFigures {
  const wrapper = isIngestionResult(result) ? result : null;
  const bundle: PremiumDealAnalysis | null | undefined = wrapper
    ? wrapper.analysis
    : (result as PremiumDealAnalysis | null | undefined);
  const score = bundle?.scorecard?.score;
  const spine = bundle?.deterministic?.spine;
  return {
    recommendation: score?.recommendation ?? score?.verdict ?? null,
    overallScore: numberOrNull(score?.overallScore),
    dscr: numberOrNull(spine?.dscr?.value),
    yieldOnCostPct: numberOrNull(spine?.yieldOnCostPct),
    // A manual run ingested no documents and carries no list — null, not zero. "Read none" and "read
    // an unknown number" would otherwise render as the same fact.
    documentCount: wrapper?.documentIds ? wrapper.documentIds.length : null,
  };
}

function numberOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Compare the previous version against this one, field by field.
 *
 * Three outcomes per field and they are kept distinct all the way to the page: CHANGED (both stated,
 * and what the reader sees differs), unchanged (both stated, and it does not — reported by omission),
 * and NOT COMPARABLE (either side was silent). Only the third is at risk of being mistaken for the
 * second, which is why it is carried explicitly rather than dropped.
 */
export function compareUnderwrites(
  previous: PreviousUnderwrite,
  current: UnderwriteFigures,
): UnderwriteComparison {
  const changes: FieldChange[] = [];
  const notComparable: FieldNotComparable[] = [];
  for (const field of FIELDS) {
    const before = previous[field];
    const after = current[field];
    if (before === null || before === undefined || after === null || after === undefined) {
      const why: Absence =
        before === null || before === undefined
          ? after === null || after === undefined
            ? 'absent-both'
            : 'absent-then'
          : 'absent-now';
      notComparable.push({ field, label: LABELS[field], why });
      continue;
    }
    const from = FORMAT[field](before);
    const to = FORMAT[field](after);
    if (from !== to) changes.push({ field, label: LABELS[field], from, to });
  }
  return { changes, notComparable };
}

const ABSENCE_TEXT: Record<Absence, string> = {
  'absent-then': 'the earlier underwrite did not state it',
  'absent-now': 'this underwrite does not state it',
  'absent-both': 'neither underwrite states it',
};

/**
 * Did the previous run actually produce an underwrite?
 *
 * 🔴 A `needs_more_docs` RUN IS STORED `succeeded`. A package too thin to underwrite completes the
 * row normally — same status, same `completed_at` — and carries no scorecard at all. It is therefore
 * a legitimate predecessor, and without this the block would head itself "what changed since the
 * previous UNDERWRITE" about a run that never underwrote, and then print four separate
 * could-not-be-compared lines to say one thing.
 *
 * BOTH must be absent, deliberately: a scorecard that produced a score but no verdict (or the
 * reverse) is a damaged underwrite, not a missing one, and the field-by-field report is the right
 * treatment for it. Requiring both keeps the inference conservative — and the one other shape that
 * satisfies it, a total scorecard failure, is described correctly by the same sentence.
 */
function producedNoUnderwrite(previous: PreviousUnderwrite): boolean {
  return previous.recommendation === null && previous.overallScore === null;
}

/**
 * The block itself.
 *
 * ⚠️ FIVE STATES, AND SILENCE IS CORRECT FOR ONLY ONE. An UNBOUND run has no project, so there is no
 * chain and the question was never asked — it says nothing, because "nothing changed" there would be
 * a clean produced by the absence of the check. A bound run with no predecessor is the FIRST
 * underwrite and says so. A predecessor that never underwrote is named as that, in one sentence
 * rather than four. Otherwise the block either lists what moved, or states that nothing it compares
 * did — and in that last case it NAMES what it compared, so a reader cannot read "nothing changed"
 * as a claim about the whole deal.
 */
export function ReportChanges({
  previous,
  current,
  projectCode,
}: {
  previous?: PreviousUnderwrite;
  current: AnalysisResult | null;
  projectCode: string | null;
}) {
  const navigate = useNavigate();
  // Never asked — an unbound run has no previous version to have changed from.
  if (!projectCode) return null;
  if (!previous) {
    return (
      <p className="report-changes report-changes--first">
        This is the first underwrite of {projectCode}.
      </p>
    );
  }

  const { changes, notComparable } = compareUnderwrites(previous, figuresOf(current));
  const when = previous.completedAt ? timestamp(previous.completedAt) : null;
  const since = when ? `the previous underwrite on ${when}` : 'the previous underwrite';
  /*
    🔴 A predecessor that never underwrote. Said once, and the per-field gaps are suppressed: they
    all have the same cause, and four lines repeating it would bury the one fact that explains them.
    Any figure that IS comparable — the document count, typically — still shows, because "we read
    three more documents and could underwrite this time" is exactly the change a reader came for.
  */
  const noBaseline = producedNoUnderwrite(previous);
  // Named, not counted. "Nothing changed" is only honest alongside the scope it covers — this block
  // reads five figures and a deal can move in ways none of them show. The labels keep their own
  // casing: lower-casing the list turned the DSCR acronym into "dscr" on the page.
  const missing = new Set(notComparable.map((n) => n.field));
  const comparedLabels = FIELDS.filter((f) => !missing.has(f)).map((f) => LABELS[f]);

  return (
    <section className="report-changes" aria-label="What changed since the previous underwrite">
      <h2 className="report-changes__head">
        {noBaseline
          ? `Compared with ${when ? `the previous run on ${when}` : 'the previous run'}`
          : changes.length > 0
            ? `What changed since ${since}`
            : `Compared with ${since}`}
      </h2>

      {noBaseline && (
        <p className="report-changes__none">
          That run produced no underwrite — there is no earlier verdict or score to compare against.
        </p>
      )}

      {changes.length > 0 ? (
        <ul className="report-changes__list">
          {changes.map((c) => (
            <li key={c.field} className="report-changes__row">
              <span className="report-changes__label">{c.label}</span>
              <span className="report-changes__from">{c.from}</span>
              <span className="report-changes__arrow" aria-hidden="true">
                →
              </span>
              <span className="report-changes__to">{c.to}</span>
            </li>
          ))}
        </ul>
      ) : (
        !noBaseline && (
          /*
            ⚠️ "NOTHING WE COMPARE" — not "nothing changed". The narrower sentence is the true one:
            this block reads five figures, and a deal can move in ways none of them show. The list of
            what was compared is rendered on exactly this state, so the scope of the claim travels
            with the claim rather than living in someone's head.
          */
          <p className="report-changes__none">
            {comparedLabels.length > 0
              ? `Unchanged: ${comparedLabels.join(', ')}.`
              : 'Nothing could be compared — the two underwrites state no figure in common.'}
          </p>
        )
      )}

      {!noBaseline && notComparable.length > 0 && (
        /*
          🔴 STATED, NEVER DROPPED. A field only one side reports is the single case where a silent
          block would be actively misleading — the reader would take the omission for steadiness.
        */
        <ul className="report-changes__gaps">
          {notComparable.map((n) => (
            <li key={n.field}>
              {n.label} could not be compared — {ABSENCE_TEXT[n.why]}.
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className="link-btn"
        onClick={() => void navigate(`/analyses/${previous.analysisId}`)}
      >
        {noBaseline ? 'Open the previous run' : 'Open the previous underwrite'}
      </button>
    </section>
  );
}
